import { useState, useEffect, useCallback, useRef } from 'react';
import { streamChat, type ChatEvent, type ScreenData } from '../api/aiChat';
import { isAdmin } from '../api/client';
import { sendCopilotMessage } from './CopilotPanel';

/**
 * AIFloatingBuddy — proactive AI assistant that appears on all pages.
 *
 * Features:
 * - Animated floating button (bottom-right)
 * - Expandable panel with dynamic contextual tips
 * - Quick mini-chat for fast questions without opening full copilot
 * - Proactive toast notifications for detected issues
 * - Data-driven suggestions based on page context
 *
 * Replaces the old BuddyTip + floating badge with a unified widget.
 */

interface AIFloatingBuddyProps {
  /** Current route path, e.g. "/invoices/made" */
  currentPath: string;
  /** Human-readable page context label */
  pageContextLabel: string;
  /** Screen data for AI context */
  screenData: ScreenData;
  /** Callback to open the full copilot panel */
  onOpenCopilot: () => void;
  /** Number of AI-detected exceptions/issues */
  exceptionsCount?: number;
}

interface MiniMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface Suggestion {
  id: string;
  message: string;
  action?: string;
  actionLabel?: string;
  variant?: 'info' | 'warning' | 'action';
}

// Dynamic data-driven suggestions based on screen data
function generateDataSuggestions(path: string, screenData: ScreenData): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Check for data-based insights
  if (screenData.recordCount !== undefined && screenData.recordCount === 0) {
    if (path.startsWith('/invoices')) {
      suggestions.push({
        id: 'no-invoices',
        message: 'Sąskaitų sąrašas tuščias. Sukurkite pirmą sąskaitą faktūrą.',
        action: 'Nauja SF',
        actionLabel: '/invoices/new',
        variant: 'action',
      });
    } else if (path === '/journal') {
      suggestions.push({
        id: 'no-journal',
        message: 'Žurnalas tuščias. Sukurkite pirmą įrašą.',
        action: 'Naujas įrašas',
        actionLabel: '/journal/new',
        variant: 'action',
      });
    }
  }

  // Check active record for potential issues
  if (screenData.activeRecord) {
    const fields = screenData.activeRecord.fields;
    // Check if invoice is not registered
    if (fields['Būsena'] === 'Neregistruota' || fields['Status'] === 'Unregistered') {
      suggestions.push({
        id: 'unregistered-doc',
        message: 'Šis dokumentas nėra registruotas. Ar norite registruoti?',
        action: 'Registruoti',
        variant: 'warning',
      });
    }
  }

  // Check form fields for empty required fields
  if (screenData.formFields && screenData.formFields.length > 0) {
    const emptyFields = screenData.formFields.filter(
      f => f.type !== 'hidden' && (!f.value || f.value.trim() === '')
    );
    if (emptyFields.length > 3) {
      suggestions.push({
        id: 'incomplete-form',
        message: `Formoje trūksta ${emptyFields.length} laukų. Ar reikia pagalbos užpildant?`,
        action: 'Padėti užpildyti',
        variant: 'info',
      });
    }
  }

  // Check table data for anomalies
  if (screenData.tableData && screenData.tableData.length > 0) {
    const table = screenData.tableData[0];
    if (table && table.totalRows && table.totalRows > 100) {
      suggestions.push({
        id: 'large-table',
        message: `Rodoma ${table.totalRows} įrašų. Galite filtruoti pagal datą arba paiešką.`,
        variant: 'info',
      });
    }
  }

  return suggestions;
}

// Static contextual suggestions per route (fallback)
const STATIC_SUGGESTIONS: Record<string, Suggestion[]> = {
  '/': [
    { id: 'dashboard-1', message: 'Peržiūrėkite santrauką arba klauskite AI pagalbos.', action: 'Kaip galiu padėti?', variant: 'info' },
  ],
  '/invoices/made': [
    { id: 'sales-1', message: 'Galite kurti naują pardavimo SF, filtruoti pagal datą arba klausti AI apie pardavimus.', action: 'Nauja pardavimo SF', actionLabel: '/invoices/new', variant: 'action' },
  ],
  '/invoices/received': [
    { id: 'purchases-1', message: 'Galite registruoti pirkimo SF, tikrinti PVM arba klausti AI apie pirkimus.', action: 'Rodyti pirkimus', variant: 'info' },
  ],
  '/invoices/new': [
    { id: 'new-inv-1', message: 'Užpildykite SF duomenis. AI gali padėti rasti klientą arba užpildyti reikšmes.', action: 'Padėti užpildyti', variant: 'info' },
  ],
  '/journal': [
    { id: 'journal-1', message: 'Peržiūrėkite žurnalo įrašus arba sukurkite naują. Debeto ir kredito sumos turi sutapti.', action: 'Naujas įrašas', actionLabel: '/journal/new', variant: 'action' },
  ],
  '/journal/new': [
    { id: 'new-journal-1', message: 'Kurkite naują žurnalo įrašą. Debeto ir kredito sumos turi sutapti.', action: 'Padėti sukurti', variant: 'info' },
  ],
  '/banking': [
    { id: 'banking-1', message: 'Galite importuoti banko išklotinę arba peržiūrėti operacijas.', action: 'Importuoti banką', actionLabel: '/banking/import', variant: 'action' },
  ],
  '/banking/import': [
    { id: 'bank-import-1', message: 'Įkelkite banko failą (CSV/XML/MT940). Sistema automatiškai atpažins formatą.', variant: 'info' },
  ],
  '/accounts': [
    { id: 'accounts-1', message: 'Tvarkykite sąskaitų planą. AI gali padėti rasti tinkamą sąskaitą.', variant: 'info' },
  ],
  '/accounts/trial-balance': [
    { id: 'trial-1', message: 'Bandomasis balansas rodo sąskaitų likučius. Patikrinkite ar debetas = kreditas.', variant: 'info' },
  ],
  '/persons': [
    { id: 'persons-1', message: 'Tvarkykite kontaktus (klientai, tiekėjai). Galite pridėti naują arba ieškoti esamų.', variant: 'info' },
  ],
  '/periods/close': [
    { id: 'close-1', message: 'Periodo uždarymas — negrįžtamas veiksmas! Įsitikinkite, kad visi įrašai registruoti.', action: 'Kaip uždaryti?', variant: 'warning' },
  ],
  '/reports/vat': [
    { id: 'vat-1', message: 'PVM ataskaita rodo mokėtiną / atgautiną PVM. Galite generuoti deklaraciją.', variant: 'info' },
  ],
  '/reports/debt-aging': [
    { id: 'debt-1', message: 'Skolų senumo ataskaita padeda sekti neapmokėtas sąskaitas.', variant: 'info' },
  ],
  '/fixed-assets': [
    { id: 'assets-1', message: 'Ilgalaikio turto registravimas ir nusidėvėjimo skaičiavimas.', variant: 'info' },
  ],
  '/settings': [
    { id: 'settings-1', message: 'Sistemos nustatymai. Galite keisti mokesčių tarifus, serijas, vartotojus.', variant: 'info' },
  ],
  '/ai-audit': [
    { id: 'ai-audit-1', message: 'AI skenerių rezultatai ir audito sekimas. Peržiūrėkite problemas.', variant: 'info' },
  ],
};

const DEFAULT_SUGGESTIONS: Suggestion[] = [
  { id: 'default-1', message: 'AI asistentas paruoštas padėti. Paspauskite bet kada!', action: 'Paklausti AI', variant: 'info' },
];

// Track which routes have already shown a suggestion this session
const shownSuggestions = new Set<string>();

const BUDDY_STATE_KEY = 'a5core_buddy_state';

function getStoredState(): { expanded: boolean; miniChatOpen: boolean } {
  try {
    const raw = sessionStorage.getItem(BUDDY_STATE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { expanded: false, miniChatOpen: false };
}

export default function AIFloatingBuddy({
  currentPath,
  pageContextLabel,
  screenData,
  onOpenCopilot,
  exceptionsCount,
}: AIFloatingBuddyProps) {
  const [expanded, setExpanded] = useState(() => getStoredState().expanded);
  const admin = isAdmin();
  const [miniChatOpen, setMiniChatOpen] = useState(false);
  const [miniMessages, setMiniMessages] = useState<MiniMessage[]>([]);
  const [miniInput, setMiniInput] = useState('');
  const [isMiniStreaming, setIsMiniStreaming] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentSuggestion, setCurrentSuggestion] = useState<Suggestion | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const miniMessagesEndRef = useRef<HTMLDivElement>(null);
  const miniInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Generate dynamic suggestions based on screen data + static fallback
  const getSuggestionsForPath = useCallback((path: string, data: ScreenData): Suggestion[] => {
    // First try data-driven suggestions
    const dataSuggestions = generateDataSuggestions(path, data);
    if (dataSuggestions.length > 0) return dataSuggestions;

    // Then try static suggestions
    if (STATIC_SUGGESTIONS[path]) return STATIC_SUGGESTIONS[path];

    // Dynamic route matching
    if (path.startsWith('/invoices/') && path.includes('/edit'))
      return STATIC_SUGGESTIONS['/invoices/made'] || DEFAULT_SUGGESTIONS;
    if (path.startsWith('/journal/') && path.endsWith('/edit'))
      return STATIC_SUGGESTIONS['/journal'] || DEFAULT_SUGGESTIONS;
    if (path.startsWith('/invoices/'))
      return STATIC_SUGGESTIONS['/invoices/new'] || DEFAULT_SUGGESTIONS;
    if (path.startsWith('/journal/'))
      return STATIC_SUGGESTIONS['/journal'] || DEFAULT_SUGGESTIONS;

    return DEFAULT_SUGGESTIONS;
  }, []);

  // Show proactive suggestion on route change
  useEffect(() => {
    setDismissed(false);

    if (shownSuggestions.has(currentPath)) {
      setCurrentSuggestion(null);
      return;
    }

    const suggestions = getSuggestionsForPath(currentPath, screenData);
    if (suggestions.length > 0) {
      const pickedSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
      if (pickedSuggestion) {
        setCurrentSuggestion(pickedSuggestion);

        const showTimer = setTimeout(() => {
          if (!expanded) {
            setToastVisible(true);
            setToastMessage(pickedSuggestion.message);
          }
          shownSuggestions.add(currentPath);
        }, 1800);

        const hideTimer = setTimeout(() => {
          setToastVisible(false);
        }, 14000);

        return () => {
          clearTimeout(showTimer);
          clearTimeout(hideTimer);
        };
      }
    }
  }, [currentPath, screenData, getSuggestionsForPath, expanded]);

  // Show exception toast
  useEffect(() => {
    if (exceptionsCount != null && exceptionsCount > 0 && !shownSuggestions.has('_exceptions')) {
      const timer = setTimeout(() => {
        if (!expanded) {
          setToastVisible(true);
          setToastMessage(`AI aptiko ${exceptionsCount} ${exceptionsCount === 1 ? 'problema' : 'problemas'}. Peržiūrėkite.`);
          shownSuggestions.add('_exceptions');

          setTimeout(() => setToastVisible(false), 10000);
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [exceptionsCount, expanded]);

  // Store expanded state
  useEffect(() => {
    try {
      sessionStorage.setItem(BUDDY_STATE_KEY, JSON.stringify({ expanded, miniChatOpen }));
    } catch { /* ignore */ }
  }, [expanded, miniChatOpen]);

  // Focus mini-chat input when it opens
  useEffect(() => {
    if (miniChatOpen) {
      setTimeout(() => miniInputRef.current?.focus(), 100);
    }
  }, [miniChatOpen]);

  // Auto-scroll mini messages
  useEffect(() => {
    miniMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [miniMessages]);

  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev);
    setToastVisible(false);
  }, []);

  const handleSuggestionAction = useCallback((suggestion: Suggestion) => {
    setToastVisible(false);
    setDismissed(true);
    setCurrentSuggestion(null);

    if (suggestion.actionLabel && suggestion.actionLabel.startsWith('/')) {
      // Navigate to path
      window.location.href = suggestion.actionLabel;
    } else if (suggestion.action) {
      // Open copilot with the action text
      sendCopilotMessage(suggestion.action);
      onOpenCopilot();
    }
  }, [onOpenCopilot]);

  const handleDismissToast = useCallback(() => {
    setToastVisible(false);
    setDismissed(true);
    setCurrentSuggestion(null);
  }, []);

  const handleOpenCopilot = useCallback(() => {
    setExpanded(false);
    onOpenCopilot();
  }, [onOpenCopilot]);

  const handleOpenMiniChat = useCallback(() => {
    setMiniChatOpen(true);
    setMiniMessages([]);
  }, []);

  const handleCloseMiniChat = useCallback(() => {
    setMiniChatOpen(false);
    setIsMiniStreaming(false);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  const handleMiniSend = useCallback(async () => {
    const text = miniInput.trim();
    if (!text || isMiniStreaming) return;

    setMiniInput('');
    const userMsg: MiniMessage = {
      id: `mini-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMiniMessages(prev => [...prev, userMsg]);
    setIsMiniStreaming(true);

    const assistantId = `mini-assistant-${Date.now()}`;
    const assistantMsg: MiniMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMiniMessages(prev => [...prev, assistantMsg]);
    let accumulated = '';

    abortRef.current = new AbortController();

    try {
      await streamChat({
        message: text,
        currentPage: `${currentPath} - ${pageContextLabel}`,
        screenData,
        onEvent: (event: ChatEvent) => {
          if (event.type === 'text' && event.content) {
            accumulated += event.content;
            setMiniMessages(prev =>
              prev.map(m => m.id === assistantId ? { ...m, content: accumulated } : m)
            );
          }
          if (event.type === 'done') {
            setIsMiniStreaming(false);
          }
        },
        onError: () => {
          accumulated += ' [Klaida: nepavyko pasiekti AI]';
          setMiniMessages(prev =>
            prev.map(m => m.id === assistantId ? { ...m, content: accumulated } : m)
          );
          setIsMiniStreaming(false);
        },
        onComplete: () => {
          setIsMiniStreaming(false);
        },
      });
    } catch {
      setMiniMessages(prev =>
        prev.map(m => m.id === assistantId ? { ...m, content: accumulated || 'Nepavyko pasiekti AI tarnybos.' } : m)
      );
      setIsMiniStreaming(false);
    }
  }, [miniInput, isMiniStreaming, currentPath, pageContextLabel, screenData]);

  const handleMiniKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleMiniSend();
    }
    if (e.key === 'Escape') {
      handleCloseMiniChat();
    }
  };

  const handleContinueInCopilot = useCallback(() => {
    // Move mini-chat conversation to full copilot
    const lastUserMsg = miniMessages.filter(m => m.role === 'user').pop();
    setMiniChatOpen(false);
    setExpanded(false);
    if (lastUserMsg) {
      sendCopilotMessage(lastUserMsg.content);
    }
    onOpenCopilot();
  }, [miniMessages, onOpenCopilot]);

  // Get suggestions for the expanded panel
  const panelSuggestions = getSuggestionsForPath(currentPath, screenData);

  return (
    <>
      {/* Proactive toast notification */}
      {toastVisible && !expanded && !dismissed && (
        <div
          className="fixed bottom-20 right-4 z-40 max-w-xs animate-slide-in"
          role="alert"
          aria-live="polite"
        >
          <div className="bg-white border border-primary-200 rounded-xl shadow-lg overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border-b border-primary-100">
              <div className="relative">
                <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {/* Pulse ring animation */}
                <span className="absolute inset-0 rounded-full animate-ping bg-primary-400 opacity-20" />
              </div>
              <span className="text-xs font-semibold text-primary-700">AI Buddy</span>
              <button
                onClick={handleDismissToast}
                className="ml-auto text-primary-400 hover:text-primary-600 transition-colors"
                aria-label="Uždaryti"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-3 py-2">
              <p className="text-xs text-gray-700 leading-relaxed">{toastMessage}</p>
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => currentSuggestion ? handleSuggestionAction(currentSuggestion) : handleOpenCopilot()}
                  className="flex-1 text-xs font-medium text-primary-600 hover:text-primary-800 bg-primary-50 hover:bg-primary-100 rounded-lg px-3 py-1.5 transition-colors text-center"
                >
                  {currentSuggestion?.action || 'Paklausti AI'}
                </button>
                <button
                  onClick={handleToggle}
                  className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 transition-colors"
                >
                  Išskleisti
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expanded buddy panel */}
      {expanded && (
        <div
          className="fixed bottom-4 right-4 z-40 w-80 max-h-[70vh] flex flex-col bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden animate-slide-in"
          role="complementary"
          aria-label="AI Buddy"
        >
          {/* Panel header */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-700 text-white">
            <div className="relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="absolute inset-0 rounded-full animate-pulse bg-white opacity-20" />
            </div>
            <span className="text-sm font-semibold">AI Buddy</span>
            <span className="text-xs text-primary-100 truncate max-w-[120px]">{pageContextLabel}</span>
            <button
              onClick={() => setExpanded(false)}
              className="ml-auto text-primary-200 hover:text-white transition-colors"
              aria-label="Suskleisti"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Panel body — scrollable */}
          <div className="flex-1 overflow-y-auto">
            {!miniChatOpen ? (
              <div className="p-3 space-y-3">
                {/* Contextual suggestions */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Pasiūlymai ({pageContextLabel})
                  </h3>
                  <div className="space-y-2">
                    {panelSuggestions.slice(0, 3).map(suggestion => (
                      <div
                        key={suggestion.id}
                        className={`rounded-lg border p-2.5 transition-colors ${
                          suggestion.variant === 'warning'
                            ? 'border-amber-200 bg-amber-50'
                            : suggestion.variant === 'action'
                              ? 'border-primary-200 bg-primary-50'
                              : 'border-gray-100 bg-gray-50'
                        }`}
                      >
                        <p className="text-xs text-gray-700 leading-relaxed">{suggestion.message}</p>
                        {suggestion.action && (
                          <button
                            onClick={() => handleSuggestionAction(suggestion)}
                            className="mt-1.5 text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors"
                          >
                            {suggestion.action} →
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick actions */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Greiti veiksmai
                  </h3>
                  <div className="grid grid-cols-2 gap-1.5">
                    <button
                      onClick={handleOpenMiniChat}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      Greitas klausimas
                    </button>
                    <button
                      onClick={handleOpenCopilot}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                    >
                      <svg className="w-3.5 h-3.5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      Pilnas pokalbis
                    </button>
                  </div>
                </div>

                {/* Exception warnings — admin-only (the only affordance opens /ai-audit) */}
                {admin && exceptionsCount != null && exceptionsCount > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2.5">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-xs font-medium text-red-700">
                        AI aptiko {exceptionsCount} {exceptionsCount === 1 ? 'problema' : 'problemas'}
                      </span>
                    </div>
                    <button
                      onClick={() => { window.location.href = '/ai-audit'; }}
                      className="mt-1.5 text-xs font-medium text-red-600 hover:text-red-800 transition-colors"
                    >
                      Peržiūrėti problemas →
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Mini-chat interface */
              <div className="flex flex-col h-full min-h-[200px] max-h-[50vh]">
                {/* Mini-chat header */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                  <button
                    onClick={handleCloseMiniChat}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Atgal"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-xs font-medium text-gray-700">Greitas klausimas</span>
                  <button
                    onClick={handleContinueInCopilot}
                    className="ml-auto text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors"
                  >
                    Tęsti pokalbį →
                  </button>
                </div>

                {/* Mini messages */}
                <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
                  {miniMessages.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-400">
                        Užduokite greitą klausimą apie apskaitą...
                      </p>
                      <p className="text-[10px] text-amber-500 mt-2">
                        ⚠ Pranešimus apdoroja išorinė AI paslauga
                      </p>
                    </div>
                  )}
                  {miniMessages.map(msg => (
                    <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-xs leading-relaxed ${
                          msg.role === 'user'
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {msg.content || (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                            Galvoja...
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={miniMessagesEndRef} />
                </div>

                {/* Mini input */}
                <div className="border-t border-gray-100 p-2">
                  <div className="flex gap-1.5">
                    <input
                      ref={miniInputRef}
                      value={miniInput}
                      onChange={(e) => setMiniInput(e.target.value)}
                      onKeyDown={handleMiniKeyDown}
                      placeholder="Klauskite..."
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      disabled={isMiniStreaming}
                    />
                    <button
                      onClick={handleMiniSend}
                      disabled={!miniInput.trim() || isMiniStreaming}
                      className="flex-shrink-0 p-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating buddy button */}
      <button
        onClick={handleToggle}
        className={`fixed bottom-4 right-4 z-30 group relative w-12 h-12 rounded-full text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center transition-all ${
          expanded
            ? 'bg-gray-600 hover:bg-gray-700'
            : 'bg-gradient-to-br from-primary-500 to-primary-700'
        }`}
        title={`AI Buddy — ${pageContextLabel}`}
        aria-label={expanded ? 'Suskleisti AI Buddy' : 'Išskleisti AI Buddy'}
      >
        {expanded ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {/* Pulse ring when not expanded */}
            {!toastVisible && (
              <span className="absolute inset-0 rounded-full animate-ping bg-primary-300 opacity-20 pointer-events-none" />
            )}
          </>
        )}

        {/* Exception count badge */}
        {!expanded && exceptionsCount != null && exceptionsCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[9px] font-bold text-white bg-red-500 rounded-full ring-2 ring-white">
            {exceptionsCount > 9 ? '9+' : exceptionsCount}
          </span>
        )}
      </button>
    </>
  );
}
